/**
 * The conversation on a ticket: what the account wrote, what PazNetworks wrote
 * back, and the box to reply.
 *
 * Staff viewing as a customer can read the thread but cannot post — a reply
 * sent while impersonating would be published under the customer's name. That
 * is the same rule the visit planner applies to its writes.
 */
import { useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { formatDateTime } from '../lib/formatters';
import { MAX_ATTACHMENT_BYTES, useTicketComments } from '../hooks/useTicketComments';
import { useDocumentDownload } from '../hooks/useDocuments';
import { Freshness } from './Freshness';

const MAX_BODY = 4000;

export function TicketConversation({
  ticketId,
  customerId,
}: {
  ticketId: string | undefined;
  customerId: string | null | undefined;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const download = useDocumentDownload();
  const { comments, isLoading, isError, readOnly, post, isPosting, postError, streaming, lastSyncedAt } =
    useTicketComments(ticketId, customerId);

  const trimmed = draft.trim();
  const canPost = !readOnly && !isPosting && trimmed.length > 0 && trimmed.length <= MAX_BODY;

  const tooLarge = !!attachment && attachment.size > MAX_ATTACHMENT_BYTES;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canPost || tooLarge) return;
    // Clear the composer only once the reply is saved. It used to clear
    // unconditionally, because post() swallowed failures — so a reply that did
    // not go through showed its error beside an empty box, and the customer's
    // message and chosen file were simply gone.
    const saved = await post(draft, attachment);
    if (!saved) return;
    setDraft('');
    setAttachment(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="ticket-conversation">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id="ticket-conversation" className="text-sm font-medium text-text-muted">
          {t('tickets.detail.conversation')}
        </h2>
        {/* Replies arrive over a realtime channel. The hook always knew whether
            it was subscribed and nothing showed it, so when the channel dropped,
            new replies from PazNetworks simply stopped appearing with no sign
            that the thread had gone stale. Invariant 5: a channel-fed surface
            reads offline while the channel is not subscribed. */}
        <Freshness
          compact
          lastUpdatedAt={lastSyncedAt}
          forcedState={streaming ? 'live' : 'offline'}
          streaming={streaming}
          label={t('tickets.detail.conversation')}
        />
      </div>

      {isLoading && <p className="text-sm text-text-muted">{t('common.loading')}</p>}
      {isError && (
        <p role="alert" className="text-sm text-notification">
          {t('tickets.detail.conversation_error')}
        </p>
      )}

      {!isLoading && !isError && comments.length === 0 && (
        <p className="text-sm text-text-faint">{t('tickets.detail.conversation_empty')}</p>
      )}

      {comments.length > 0 && (
        <ol className="mb-4 space-y-3">
          {comments.map((comment) => {
            const fromStaff = comment.author_kind === 'staff';
            return (
              <li
                key={comment.id}
                className={`rounded-md border px-3 py-2.5 ${
                  fromStaff ? 'border-primary-soft bg-primary-soft/40' : 'border-border bg-surface-2'
                }`}
              >
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="text-xs font-semibold text-text">{comment.author_name}</span>
                  <span className="text-[11px] text-text-faint">{formatDateTime(comment.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-text">{comment.body}</p>
                {comment.attachment && (
                  <button
                    type="button"
                    onClick={() => download.mutate(comment.attachment!)}
                    className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 text-xs text-primary hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-8"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="truncate">{comment.attachment.title}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {readOnly ? (
        <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-xs text-text-muted">
          {t('tickets.detail.conversation_read_only')}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-2">
          <label htmlFor="ticket-reply" className="block text-xs font-medium text-text-muted">
            {t('tickets.detail.reply_label')}
          </label>
          <textarea
            id="ticket-reply"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            maxLength={MAX_BODY}
            placeholder={t('tickets.detail.reply_placeholder')}
            className="w-full rounded-sm border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {tooLarge && (
            <p role="alert" className="text-xs text-notification">
              {t('tickets.detail.attach_too_large')}
            </p>
          )}
          {postError && (
            <p role="alert" className="text-xs text-notification">
              {postError === 'too_large'
                ? t('tickets.detail.attach_too_large')
                : t('tickets.detail.reply_error')}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <label
                htmlFor="ticket-attachment"
                className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-surface px-3 text-xs text-text-muted hover:bg-surface-3 sm:min-h-9"
              >
                <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {t('tickets.detail.attach')}
              </label>
              <input
                ref={fileRef}
                id="ticket-attachment"
                type="file"
                accept="image/*,application/pdf,text/plain"
                className="sr-only"
                onChange={(event) => setAttachment(event.target.files?.[0] ?? null)}
              />
              {attachment && (
                <span className="flex min-w-0 items-center gap-1.5 text-xs text-text-muted">
                  <span className="truncate">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachment(null);
                      if (fileRef.current) fileRef.current.value = '';
                    }}
                    aria-label={t('tickets.detail.attach_remove', { name: attachment.name })}
                    className="rounded-sm px-1 text-text-faint hover:text-notification focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={!canPost || tooLarge}
              className="inline-flex min-h-11 items-center rounded-md bg-primary px-4 text-sm font-medium text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9"
            >
              {isPosting ? t('tickets.detail.reply_sending') : t('tickets.detail.reply_send')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
